import { FormElementControlPropsType } from 'components/common/form/layout/control';
import { Grid, IconButton, Input, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import { FormControlPageVariablesProps, VariableData } from './index';
import UploadFile from 'components/common/upload-control/UploadFile';
import { pageAPI } from 'api/editor/page';
import ColorPicker from 'components/common/color-picker/ColorPicker';
import ListType from 'app/editor/page/variables/ListType';
import PageVariable from 'app/editor/page/variables/PageVariable';
import AsyncReactSelect from 'components/common/react-select/AsyncReactSelect';
import TextTinyMCEVariable from './TextTinyMCEVariable';
import TextHtmlVariable from './TextHtmlVariable';
import IconMi from 'components/common/icon/IconMi';
import { Popup, PopupSettings } from 'components/common/popup/Popup';
import useBundleTranslation from 'i18n';
import FormRadioGroup from 'components/common/form/layout/control/FormRadioGroup';
import { usePageVariablesOptions, SELECT_LIMIT } from './usePageVariablesOptions';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import { FormComponentBuilder } from 'components/common/form/layout';
import { getPkId } from 'components/common/form/formTools';

export default function PageVariables({ controlProps }: FormElementControlPropsType<FormControlPageVariablesProps>) {
    const { t } = useBundleTranslation(['app/editor/page-template/assets_tree', 'app/editor/page-template/variable']);

    const prepareValues = (data: string) => {
        const list = !data ? [{ value: null }] : JSON.parse(data);
        controlProps.value = list.map((v: any) => {
            if (controlProps.listOptions) {
                v = {
                    ...v,
                    portal_page_template_tag_id: 0,
                    portal_page_template_id: 0,
                    name: 'Sections',
                    uid: 'list_data_component',
                    default_value: '',
                    additional_options: controlProps.listOptions,
                    tag_type: 'list',
                    tag_source: 'static',
                    javascript_code: '',
                    display_order: 0,
                    use_hmtl_editor_ind: 'N',
                    global_tag_id: null,
                    use_json_editor_ind: 'N',
                    description: '',
                    use_js_code_ind: 'N',
                    use_raw_html_ind: 'N',
                    portal_page_asset_id: null,
                };
            }
            return v;
        });
        return controlProps.value;
    };

    const [showLoader, setShowLoader] = useState(false);
    const [pageVariables, setPageVariables] = useState<VariableData[]>(
        typeof controlProps.value == 'string' ? prepareValues(controlProps.value) : controlProps.value,
    );
    const [assetIndex2Delete, setAssetIndex2Delete] = useState(-1);

    const { loadOptions } = usePageVariablesOptions({
        portalPageId: controlProps.pkId,
        loadSelectDataUrl: controlProps.loadSelectDataUrl,
        controlPropsValue: controlProps.value,
    });

    const confirmPopupSettings: PopupSettings = {
        title: t('proceed_action'),
        textOK: t('Ok'),
    };

    const updateValue = (index: number, value: string | string[]) => {
        const variablesData = pageVariables.slice();

        const newValue = Array.isArray(value) ? value.join(',') : value;
        if (variablesData[index].value !== newValue) {
            variablesData[index].value = newValue;
            setPageVariables(variablesData);
            controlProps.onChange(JSON.stringify(variablesData));
        }
    };

    const updateAssetId = (index: number, assetId: string) => {
        const variablesData = pageVariables.slice();

        variablesData[index].portal_page_asset_id = `[${assetId}]`;
        setPageVariables(variablesData);
        controlProps.onChange(JSON.stringify(variablesData));
    };

    const uploadButtonClick = (event: React.ChangeEvent<HTMLInputElement>, tagUid: string, index: number) => {
        if (!event.target.files || controlProps.uploadAssetUrl === '') {
            return;
        }

        setShowLoader(true);
        const file = event.target.files[0];
        const formData = new FormData();
        formData.append('file', file, file.name);
        const uploadUrl = `${controlProps.uploadAssetUrl}/${tagUid}`;

        pageAPI
            .uploadAsset(formData, uploadUrl)
            .then((response) => {
                if (response.status === 'OK') {
                    updateValue(index, response.file_name);
                    updateAssetId(index, response.portal_page_asset_id);
                }
            })
            .finally(() => {
                setShowLoader(false);
            });
    };

    const removeAssetConfirm = () => {
        if (controlProps.assetDeleteUrl === '' || assetIndex2Delete === null) {
            return;
        }

        const assetsIds = JSON.parse(pageVariables[assetIndex2Delete].portal_page_asset_id);
        const assetId = Array.isArray(assetsIds) ? assetsIds[0] : '';

        if (assetId > '') {
            setShowLoader(true);

            pageAPI
                .deleteAsset(assetId, controlProps.assetDeleteUrl)
                .then((response) => {
                    updateValue(assetIndex2Delete, '');
                    updateAssetId(assetIndex2Delete, '');
                })
                .finally(() => {
                    setShowLoader(false);
                    setAssetIndex2Delete(-1);
                });
        }
    };

    return controlProps.value && Array.isArray(pageVariables) ? (
        <>
            <input
                name={controlProps.name}
                type={'hidden'}
                value={JSON.stringify(controlProps.value)}
                onChange={controlProps.onChange}
            />
            <Stack id={controlProps.pkId} key={controlProps.pkId + '-page-variables'} spacing={2}>
                {pageVariables.map((tag, index) => {
                    if (tag.tag_type === 'text') {
                        if (tag.use_hmtl_editor_ind === 'Y') {
                            return (
                                <PageVariable tag={tag} key={tag.uid + '-text'}>
                                    <TextTinyMCEVariable
                                        name={tag.name}
                                        value={tag.value}
                                        onChange={(newValue) => updateValue(index, newValue)}
                                    />
                                </PageVariable>
                            );
                        }

                        if (tag.use_raw_html_ind === 'Y') {
                            return (
                                <PageVariable tag={tag} key={tag.uid + '-text'}>
                                    <TextHtmlVariable
                                        name={tag.name}
                                        value={tag.value}
                                        onChange={(newValue) => updateValue(index, newValue)}
                                    />
                                </PageVariable>
                            );
                        }

                        return (
                            <PageVariable tag={tag} key={tag.uid + '-text'}>
                                <Input
                                    value={tag.value}
                                    onChange={(event) => {
                                        updateValue(index, event.target.value);
                                    }}
                                    multiline
                                    minRows={1}
                                    inputProps={{ 'data-1p-ignore': true }}
                                />
                            </PageVariable>
                        );
                    }
                    if (tag.tag_type === 'color') {
                        return (
                            <PageVariable tag={tag} key={tag.uid + '-color'}>
                                <ColorPicker
                                    controlProps={{
                                        ...controlProps,
                                        value: tag.value,
                                        name: tag.name,
                                        onColorChange: (color) => {
                                            updateValue(index, color);
                                        },
                                        simpleMode: true,
                                    }}
                                />
                            </PageVariable>
                        );
                    }
                    if (tag.tag_type === 'boolean') {
                        return (
                            <PageVariable tag={tag} key={tag.uid + '-boolean'}>
                                <FormRadioGroup
                                    controlProps={{
                                        ...controlProps,
                                        value: tag.value,
                                        isRow: true,
                                        data: [
                                            { label: t('true_value'), value: 'true' },
                                            { label: t('false_value'), value: 'false' },
                                        ],
                                        onChange: (event) => {
                                            updateValue(index, event.target.value);
                                        },
                                    }}
                                />
                            </PageVariable>
                        );
                    }
                    if (tag.tag_type === 'file') {
                        return (
                            <PageVariable tag={tag} key={tag.uid + '-file'}>
                                <Grid container direction={'row'} spacing={2} alignItems={'center'}>
                                    <Grid item xs={3}>
                                        <UploadFile
                                            key={tag.uid + '-upload-file'}
                                            controlProps={{
                                                ...controlProps,
                                                accept: 'image/*',
                                                onChange: (event) => {
                                                    uploadButtonClick(event, tag.uid, index);
                                                },
                                                showRemoveAction: false,
                                                maxFileSizeMB: 512,
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={8}>
                                        <Typography>{tag.value}</Typography>
                                    </Grid>
                                    <Grid item xs={1}>
                                        {tag.value > '' && tag.portal_page_asset_id && (
                                            <IconButton size={'small'} onClick={() => setAssetIndex2Delete(index)}>
                                                <IconMi icon="delete" fontSize="16" />
                                            </IconButton>
                                        )}
                                    </Grid>
                                </Grid>
                            </PageVariable>
                        );
                    }
                    if (tag.tag_type === 'select' || tag.tag_type === 'multiselect') {
                        const isMulti = tag.tag_type === 'multiselect';

                        return (
                            <PageVariable tag={tag} key={tag.uid + '-select'}>
                                <AsyncReactSelect
                                    value={isMulti ? tag.value.split(',') : tag.value}
                                    update={(value: any) => {
                                        updateValue(index, value);
                                    }}
                                    sortValues={false}
                                    loadOptions={(inputValue, init) =>
                                        loadOptions(
                                            inputValue,
                                            tag.additional_options,
                                            tag.tag_source,
                                            tag.value,
                                            isMulti ? 'Y' : 'N',
                                            init,
                                        )
                                    }
                                    isMulti={isMulti}
                                    preloadOptions
                                    optionsLimit={tag.tag_source !== 'static' ? SELECT_LIMIT : undefined}
                                />
                            </PageVariable>
                        );
                    }
                    if (tag.tag_type === 'list') {
                        return (
                            <ListType
                                key={tag.uid + '-list'}
                                tag={tag}
                                controlProps={controlProps}
                                updateValue={updateValue}
                                tagIndex={index}
                                setShowLoader={setShowLoader}
                                loadOptions={loadOptions}
                            />
                        );
                    }
                })}
                {assetIndex2Delete > -1 && (
                    <Popup
                        maxWidth={'popupSm'}
                        settings={confirmPopupSettings}
                        open={assetIndex2Delete > -1}
                        onHide={() => setAssetIndex2Delete(-1)}
                        onConfirm={removeAssetConfirm}
                    >
                        <Typography>{t('delete_confirmation')}</Typography>
                    </Popup>
                )}
            </Stack>
            <LoadingPlaceholder open={showLoader} />
        </>
    ) : null;
}

export class PageVariablesBuilder extends FormComponentBuilder {
    prepareProps(): FormControlPageVariablesProps {
        //@ts-ignore
        return {
            ...this.controlProps,
            pkId: getPkId(this.elementProps.pk, this.elementProps.urlParams),
            uploadAssetUrl: this.elementProps.componentProps?.uploadAssetUrl ?? '',
            loadSelectDataUrl: this.elementProps.componentProps?.loadSelectDataUrl ?? '',
            assetDeleteUrl: this.elementProps.componentProps?.assetDeleteUrl ?? '',
            listOptions: this.elementProps.componentProps?.listOptions,
        };
    }
}
